import React, { type ReactNode } from 'react'

import { Box, Divider, Flex, Heading, Progress, Text } from '@chakra-ui/react'
import { BuildingIcon, MapPinIcon, WSkeleton } from '@wanda-space/noelle'

type SizeProps = {
  size: number | string
  unit: ReactNode
}

type StorageUnitRow = {
  id: string
  sizeProps: SizeProps
  description?: ReactNode
}

type StorageUnitGroup = {
  id: string
  title?: string
  subtitle?: string
  storageUnits: StorageUnitRow[]
}

type Props = {
  title: string
  isLoadingUsage: boolean
  totalPrice: string
  priceText: string
  storageUnits?: StorageUnitRow[]
  // Takes precedence over `storageUnits`; renders the rows under a heading per group
  storageUnitGroups?: StorageUnitGroup[]
  cta?: ReactNode
}

export const SizeString = ({ size, unit }: SizeProps) => {
  return (
    <span>
      {size} {unit}
    </span>
  )
}

export const StorageUnitSubscriptionInfo = ({
  title,
  isLoadingUsage,
  totalPrice,
  priceText,
  storageUnits = [],
  storageUnitGroups,
  cta,
}: Props) => {
  const groups = storageUnitGroups ?? [{ id: 'all', storageUnits }]

  return (
    <>
      <Heading fontSize="md" as="h2">
        {title}
      </Heading>
      <Box
        mt="3"
        mb="5"
        borderRadius="6"
        p="5"
        border="1px solid"
        borderColor="gray.100"
        boxShadow="sm"
        justifyContent="center"
      >
        <Box>
          {groups.map((group, index) => (
            <Box key={group.id} mt={index > 0 ? 5 : 0}>
              {group.title && (
                <Box mb="3">
                  <Text fontWeight="500">{group.title}</Text>
                  {group.subtitle && (
                    <Flex alignItems="center" gap="1" color="gray.500" fontSize="sm">
                      <MapPinIcon width="4" height="4" />
                      <Text>{group.subtitle}</Text>
                    </Flex>
                  )}
                </Box>
              )}
              {group.storageUnits.map(({ sizeProps, id, description }) => (
                <Flex key={id} gap="3" mb={3}>
                  <Box bg="paleBlue.300" borderRadius="5" p="3">
                    <BuildingIcon color="paleBlue.900" mt="1" width="10" height="10" />
                  </Box>
                  <Box flexGrow={1}>
                    <Text fontWeight="500" fontSize="lg">
                      {isLoadingUsage ? (
                        <WSkeleton height="8" width="20" mb="1.5" />
                      ) : (
                        <SizeString {...sizeProps} />
                      )}
                    </Text>
                    <Box color="gray.500" fontSize="sm">
                      {description}
                    </Box>
                  </Box>
                </Flex>
              ))}
            </Box>
          ))}
          <Divider my="4" />
          <Flex alignItems="end" justifyContent="space-between">
            <Text color="gray.500" fontSize="xs">
              {isLoadingUsage ? <WSkeleton height="3" width="20" /> : priceText}
            </Text>
            <Box>
              <Text fontWeight="500">
                {isLoadingUsage ? <WSkeleton height="4" width="20" /> : totalPrice}
              </Text>
            </Box>
          </Flex>
          {cta && <Box mt="5">{cta}</Box>}
        </Box>
      </Box>
    </>
  )
}
